import React, { useState, useEffect, useCallback } from 'react';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, BarElement } from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import { generateInterestRateScenarios, formatCurrency, calculateAnnuityPayment } from '../utils/mortgageCalculations';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend);

interface InterestRateScenario {
  rate: number;
  monthlyPayment: number;
}

interface HistoricalRate {
  year: number;
  month: number;
  rateFixation: string;
  rate: number;
}

const InterestRatePlots: React.FC = () => {
  const [mortgageAmount, setMortgageAmount] = useState<number>(300000);
  const [term, setTerm] = useState<number>(20);
  const [baseRate, setBaseRate] = useState<number>(4.0);
  const [scenarios, setScenarios] = useState<InterestRateScenario[]>([]);
  const [currentRates, setCurrentRates] = useState<{
    month: string;
    rates: Record<string, number>;
  } | null>(null);
  const [ratesLoading, setRatesLoading] = useState<boolean>(false);
  const [historicalRates, setHistoricalRates] = useState<HistoricalRate[]>([]);
  const [historicalLoading, setHistoricalLoading] = useState<boolean>(false);
  const [selectedFixation, setSelectedFixation] = useState<string>('> 1 year and <= 5 year *');

  // Helper function to get user-friendly labels for fixation periods
  const getFixationLabel = (fixation: string) => {
    const labelMap: Record<string, string> = {
      'Static data': 'Historisch (1994-2003)',
      'Floating and <= 1 year *': '<1 jaar (variabel)',
      '> 1 year and <= 5 year *': '1-5 jaar vast',
      '> 5 year and <= 10 year *': '5-10 jaar vast',
      '> 10 year *': '>10 jaar vast',
      'Total *': 'Gemiddeld (alle periodes)'
    };
    return labelMap[fixation] || fixation;
  };
  const [availableFixations, setAvailableFixations] = useState<string[]>([]);

  // Fetch available fixation periods first
  const fetchFixationPeriods = useCallback(async () => {
    try {
      const response = await fetch('/api/dnb/fixation-periods');
      const result = await response.json();

      if (result.success && result.fixationPeriods) {
        setAvailableFixations(result.fixationPeriods);
        if (result.fixationPeriods.length > 0 && !selectedFixation) {
          setSelectedFixation(result.fixationPeriods[0]);
        }
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error fetching fixation periods:', error);
      return false;
    }
  }, [selectedFixation]);

  // Fetch historical rates from DNB API
  const fetchHistoricalRates = useCallback(async () => {
    setHistoricalLoading(true);
    try {
      // First, try to get all historical data
      const response = await fetch('/api/dnb/historical-rates');
      const result = await response.json();

      if (result.success && result.data) {
        setHistoricalRates(result.data);
        if (result.summary?.availableFixationPeriods) {
          const allFixations = ['Static data', ...result.summary.availableFixationPeriods];
          setAvailableFixations(allFixations);
          if (!selectedFixation && allFixations.length > 0) {
            setSelectedFixation('> 1 year and <= 5 year *'); // Default to most common period
          }
        }
        console.log(`Loaded ${result.data.length} historical rates from ${result.source}`);
      } else {
        console.error('Failed to fetch historical rates:', result.error);
        // Try to get fixation periods even if full data fails
        await fetchFixationPeriods();
      }
    } catch (error) {
      console.error('Error fetching historical rates:', error);
      // Try to get fixation periods even if full data fails
      await fetchFixationPeriods();
    } finally {
      setHistoricalLoading(false);
    }
  }, [selectedFixation, fetchFixationPeriods]);

  // Fetch current mortgage rates from secure backend
  const fetchCurrentRates = useCallback(async () => {
    setRatesLoading(true);
    try {
      const response = await fetch('/api/dnb/mortgage-rates');
      const result = await response.json();

      if (result.success) {
        setCurrentRates(result.data);
        // Update baseRate with the most common fixation period (1-5y)
        if (result.data.rates['1–5y']) {
          setBaseRate(result.data.rates['1–5y']);
        }
      } else {
        console.error('Failed to fetch current rates:', result.error);
      }
    } catch (error) {
      console.error('Error fetching current rates:', error);
    } finally {
      setRatesLoading(false);
    }
  }, []);

  useEffect(() => {
    const newScenarios = generateInterestRateScenarios(baseRate, mortgageAmount, term);
    setScenarios(newScenarios);
  }, [baseRate, mortgageAmount, term]);

  // Static historical data for periods before DNB API (1994-2003)
  const staticHistoricalRates: HistoricalRate[] = [
    { year: 1994, month: 1, rateFixation: 'Static data', rate: 6.93 },
    { year: 1994, month: 2, rateFixation: 'Static data', rate: 6.84 },
    { year: 1994, month: 3, rateFixation: 'Static data', rate: 6.88 },
    { year: 1994, month: 4, rateFixation: 'Static data', rate: 6.93 },
    { year: 1994, month: 5, rateFixation: 'Static data', rate: 7.01 },
    { year: 1994, month: 6, rateFixation: 'Static data', rate: 7.15 },
    { year: 1994, month: 7, rateFixation: 'Static data', rate: 7.3 },
    { year: 1994, month: 8, rateFixation: 'Static data', rate: 7.46 },
    { year: 1994, month: 9, rateFixation: 'Static data', rate: 7.49 },
    { year: 1994, month: 10, rateFixation: 'Static data', rate: 7.68 },
    { year: 1994, month: 11, rateFixation: 'Static data', rate: 7.77 },
    { year: 1994, month: 12, rateFixation: 'Static data', rate: 7.8 },
    { year: 1995, month: 1, rateFixation: 'Static data', rate: 7.75 },
    { year: 1995, month: 2, rateFixation: 'Static data', rate: 7.76 },
    { year: 1995, month: 3, rateFixation: 'Static data', rate: 7.79 },
    { year: 1995, month: 4, rateFixation: 'Static data', rate: 7.62 },
    { year: 1995, month: 5, rateFixation: 'Static data', rate: 7.39 },
    { year: 1995, month: 6, rateFixation: 'Static data', rate: 7.22 },
    { year: 1995, month: 7, rateFixation: 'Static data', rate: 7.16 },
    { year: 1995, month: 8, rateFixation: 'Static data', rate: 7.14 },
    { year: 1995, month: 9, rateFixation: 'Static data', rate: 6.9 },
    { year: 1995, month: 10, rateFixation: 'Static data', rate: 6.76 },
    { year: 1995, month: 11, rateFixation: 'Static data', rate: 6.68 },
    { year: 1995, month: 12, rateFixation: 'Static data', rate: 6.46 },
    { year: 1996, month: 1, rateFixation: 'Static data', rate: 6.25 },
    { year: 1996, month: 2, rateFixation: 'Static data', rate: 6.2 },
    { year: 1996, month: 3, rateFixation: 'Static data', rate: 6.45 },
    { year: 1996, month: 4, rateFixation: 'Static data', rate: 6.57 },
    { year: 1996, month: 5, rateFixation: 'Static data', rate: 6.54 },
    { year: 1996, month: 6, rateFixation: 'Static data', rate: 6.53 },
    { year: 1996, month: 7, rateFixation: 'Static data', rate: 6.43 },
    { year: 1996, month: 8, rateFixation: 'Static data', rate: 6.41 },
    { year: 1996, month: 9, rateFixation: 'Static data', rate: 6.24 },
    { year: 1996, month: 10, rateFixation: 'Static data', rate: 6.02 },
    { year: 1996, month: 11, rateFixation: 'Static data', rate: 5.87 },
    { year: 1996, month: 12, rateFixation: 'Static data', rate: 5.77 },
    { year: 1997, month: 1, rateFixation: 'Static data', rate: 5.76 },
    { year: 1997, month: 2, rateFixation: 'Static data', rate: 5.61 },
    { year: 1997, month: 3, rateFixation: 'Static data', rate: 5.51 },
    { year: 1997, month: 4, rateFixation: 'Static data', rate: 5.68 },
    { year: 1997, month: 5, rateFixation: 'Static data', rate: 5.96 },
    { year: 1997, month: 6, rateFixation: 'Static data', rate: 5.98 },
    { year: 1997, month: 7, rateFixation: 'Static data', rate: 5.95 },
    { year: 1997, month: 8, rateFixation: 'Static data', rate: 5.84 },
    { year: 1997, month: 9, rateFixation: 'Static data', rate: 5.75 },
    { year: 1997, month: 10, rateFixation: 'Static data', rate: 5.81 },
    { year: 1997, month: 11, rateFixation: 'Static data', rate: 5.86 },
    { year: 1997, month: 12, rateFixation: 'Static data', rate: 5.9 },
    { year: 1998, month: 1, rateFixation: 'Static data', rate: 5.93 },
    { year: 1998, month: 2, rateFixation: 'Static data', rate: 5.84 },
    { year: 1998, month: 3, rateFixation: 'Static data', rate: 5.68 },
    { year: 1998, month: 4, rateFixation: 'Static data', rate: 5.64 },
    { year: 1998, month: 5, rateFixation: 'Static data', rate: 5.64 },
    { year: 1998, month: 6, rateFixation: 'Static data', rate: 5.63 },
    { year: 1998, month: 7, rateFixation: 'Static data', rate: 5.59 },
    { year: 1998, month: 8, rateFixation: 'Static data', rate: 5.52 },
    { year: 1998, month: 9, rateFixation: 'Static data', rate: 5.45 },
    { year: 1998, month: 10, rateFixation: 'Static data', rate: 5.33 },
    { year: 1998, month: 11, rateFixation: 'Static data', rate: 5.28 },
    { year: 1998, month: 12, rateFixation: 'Static data', rate: 5.24 },
    { year: 1999, month: 1, rateFixation: 'Static data', rate: 5.15 },
    { year: 1999, month: 2, rateFixation: 'Static data', rate: 5.01 },
    { year: 1999, month: 3, rateFixation: 'Static data', rate: 4.99 },
    { year: 1999, month: 4, rateFixation: 'Static data', rate: 5.01 },
    { year: 1999, month: 5, rateFixation: 'Static data', rate: 4.97 },
    { year: 1999, month: 6, rateFixation: 'Static data', rate: 4.95 },
    { year: 1999, month: 7, rateFixation: 'Static data', rate: 5.01 },
    { year: 1999, month: 8, rateFixation: 'Static data', rate: 5.11 },
    { year: 1999, month: 9, rateFixation: 'Static data', rate: 5.23 },
    { year: 1999, month: 10, rateFixation: 'Static data', rate: 5.36 },
    { year: 1999, month: 11, rateFixation: 'Static data', rate: 5.44 },
    { year: 1999, month: 12, rateFixation: 'Static data', rate: 5.5 },
    { year: 2000, month: 1, rateFixation: 'Static data', rate: 5.56 },
    { year: 2000, month: 2, rateFixation: 'Static data', rate: 5.6 },
    { year: 2000, month: 3, rateFixation: 'Static data', rate: 5.66 },
    { year: 2000, month: 4, rateFixation: 'Static data', rate: 5.66 },
    { year: 2000, month: 5, rateFixation: 'Static data', rate: 5.67 },
    { year: 2000, month: 6, rateFixation: 'Static data', rate: 5.75 },
    { year: 2000, month: 7, rateFixation: 'Static data', rate: 5.89 },
    { year: 2000, month: 8, rateFixation: 'Static data', rate: 6.01 },
    { year: 2000, month: 9, rateFixation: 'Static data', rate: 6.11 },
    { year: 2000, month: 10, rateFixation: 'Static data', rate: 6.18 },
    { year: 2000, month: 11, rateFixation: 'Static data', rate: 6.22 },
    { year: 2000, month: 12, rateFixation: 'Static data', rate: 6.22 },
    { year: 2001, month: 1, rateFixation: 'Static data', rate: 6.15 },
    { year: 2001, month: 2, rateFixation: 'Static data', rate: 6.01 },
    { year: 2001, month: 3, rateFixation: 'Static data', rate: 5.94 },
    { year: 2001, month: 4, rateFixation: 'Static data', rate: 5.89 },
    { year: 2001, month: 5, rateFixation: 'Static data', rate: 5.86 },
    { year: 2001, month: 6, rateFixation: 'Static data', rate: 5.85 },
    { year: 2001, month: 7, rateFixation: 'Static data', rate: 5.88 },
    { year: 2001, month: 8, rateFixation: 'Static data', rate: 5.84 },
    { year: 2001, month: 9, rateFixation: 'Static data', rate: 5.74 },
    { year: 2001, month: 10, rateFixation: 'Static data', rate: 5.64 },
    { year: 2001, month: 11, rateFixation: 'Static data', rate: 5.44 },
    { year: 2001, month: 12, rateFixation: 'Static data', rate: 5.28 },
    { year: 2002, month: 1, rateFixation: 'Static data', rate: 5.29 },
    { year: 2002, month: 2, rateFixation: 'Static data', rate: 5.33 },
    { year: 2002, month: 3, rateFixation: 'Static data', rate: 5.38 },
    { year: 2002, month: 4, rateFixation: 'Static data', rate: 5.46 },
    { year: 2002, month: 5, rateFixation: 'Static data', rate: 5.54 },
    { year: 2002, month: 6, rateFixation: 'Static data', rate: 5.58 },
    { year: 2002, month: 7, rateFixation: 'Static data', rate: 5.55 },
    { year: 2002, month: 8, rateFixation: 'Static data', rate: 5.43 },
    { year: 2002, month: 9, rateFixation: 'Static data', rate: 5.3 },
    { year: 2002, month: 10, rateFixation: 'Static data', rate: 5.11 },
    { year: 2002, month: 11, rateFixation: 'Static data', rate: 5.04 },
    { year: 2002, month: 12, rateFixation: 'Static data', rate: 4.99 },
    { year: 2003, month: 1, rateFixation: 'Static data', rate: 4.87 },
    { year: 2003, month: 2, rateFixation: 'Static data', rate: 4.69 },
    { year: 2003, month: 3, rateFixation: 'Static data', rate: 4.52 },
    { year: 2003, month: 4, rateFixation: 'Static data', rate: 4.48 },
    { year: 2003, month: 5, rateFixation: 'Static data', rate: 4.51 },
    { year: 2003, month: 6, rateFixation: 'Static data', rate: 4.39 },
    { year: 2003, month: 7, rateFixation: 'Static data', rate: 4.26 },
    { year: 2003, month: 8, rateFixation: 'Static data', rate: 4.28 },
    { year: 2003, month: 9, rateFixation: 'Static data', rate: 4.35 },
    { year: 2003, month: 10, rateFixation: 'Static data', rate: 4.42 },
    { year: 2003, month: 11, rateFixation: 'Static data', rate: 4.48 },
    { year: 2003, month: 12, rateFixation: 'Static data', rate: 4.52 },
  ];

  // Combine static and API data
  const getCombinedHistoricalData = () => {
    const combined = [...staticHistoricalRates];

    // Add DNB API data, avoiding duplicates
    historicalRates.forEach(apiRate => {
      const existsInStatic = staticHistoricalRates.some(
        staticRate =>
          staticRate.year === apiRate.year &&
          staticRate.month === apiRate.month
      );

      if (!existsInStatic) {
        combined.push(apiRate);
      }
    });

    return combined.sort((a, b) => {
      if (a.year !== b.year) return a.year - b.year;
      return a.month - b.month;
    });
  };

  useEffect(() => {
    fetchCurrentRates();
    fetchHistoricalRates();
  }, [fetchCurrentRates, fetchHistoricalRates]);

  const getHistoricalChartData = () => {
    // Get combined historical data
    const combinedData = getCombinedHistoricalData();

    // Filter by selected fixation period or show static data if no specific fixation selected
    const filteredRates = selectedFixation === 'Static data'
      ? combinedData.filter(rate => rate.rateFixation === 'Static data')
      : combinedData.filter(rate => rate.rateFixation === selectedFixation);

    return {
      chartData: {
        labels: filteredRates.map(rate => `${rate.year}-${rate.month.toString().padStart(2, '0')}`),
        datasets: [
          {
            label: `Hypotheekrente (${getFixationLabel(selectedFixation)})`,
            data: filteredRates.map(rate => rate.rate),
            borderColor: 'rgb(0, 115, 230)',
            backgroundColor: 'rgba(0, 115, 230, 0.1)',
            fill: true,
            tension: 0.1
          }
        ]
      },
      filteredCount: filteredRates.length
    };
  };

  const getScenarioChartData = () => {
    return {
      labels: scenarios.map(scenario => `${scenario.rate.toFixed(1)}%`),
      datasets: [
        {
          label: 'Maandlast (€)',
          data: scenarios.map(scenario => scenario.monthlyPayment),
          backgroundColor: scenarios.map((scenario, index) => {
            if (scenario.rate === baseRate) return 'rgba(0, 115, 230, 0.8)';
            if (scenario.rate < baseRate) return 'rgba(40, 167, 69, 0.8)';
            return 'rgba(220, 53, 69, 0.8)';
          }),
          borderColor: scenarios.map((scenario, index) => {
            if (scenario.rate === baseRate) return 'rgb(0, 115, 230)';
            if (scenario.rate < baseRate) return 'rgb(40, 167, 69)';
            return 'rgb(220, 53, 69)';
          }),
          borderWidth: 2
        }
      ]
    };
  };

  const getRateComparisonData = () => {
    const years = Array.from({ length: term }, (_, i) => i + 1);

    // Calculate remaining balance for base rate over time
    const months = term * 12;
    const monthlyRate = baseRate / 100 / 12;
    const monthlyPayment = calculateAnnuityPayment(mortgageAmount, baseRate / 100, months);

    let balance = mortgageAmount;
    const balanceData = [balance];
    const interestData = [];
    const principalData = [];

    for (let year = 1; year <= term; year++) {
      let yearlyInterest = 0;
      let yearlyPrincipal = 0;

      for (let month = 1; month <= 12 && balance > 0; month++) {
        const interest = balance * monthlyRate;
        const principal = monthlyPayment - interest;
        balance = Math.max(0, balance - principal);

        yearlyInterest += interest;
        yearlyPrincipal += principal;
      }

      balanceData.push(balance);
      interestData.push(yearlyInterest);
      principalData.push(yearlyPrincipal);
    }

    return {
      labels: ['Start', ...years.map(y => `Jaar ${y}`)],
      datasets: [
        {
          label: 'Restschuld',
          data: balanceData,
          borderColor: 'rgb(0, 115, 230)',
          backgroundColor: 'rgba(0, 115, 230, 0.1)',
          fill: true,
          yAxisID: 'y'
        },
        {
          label: 'Jaarlijkse Rente',
          data: [0, ...interestData],
          borderColor: 'rgb(220, 53, 69)',
          backgroundColor: 'rgba(220, 53, 69, 0.1)',
          fill: false,
          yAxisID: 'y1'
        },
        {
          label: 'Jaarlijkse Aflossing',
          data: [0, ...principalData],
          borderColor: 'rgb(40, 167, 69)',
          backgroundColor: 'rgba(40, 167, 69, 0.1)',
          fill: false,
          yAxisID: 'y1'
        }
      ]
    };
  };

  const getCurrentMonthlyPayment = () => {
    return calculateAnnuityPayment(mortgageAmount, baseRate / 100, term * 12);
  };

  const getPaymentDifference = (rate: number) => {
    const basePayment = getCurrentMonthlyPayment();
    const scenarioPayment = calculateAnnuityPayment(mortgageAmount, rate / 100, term * 12);
    return scenarioPayment - basePayment;
  };

  const getTotalInterestDifference = (rate: number) => {
    const months = term * 12;
    const basePayment = getCurrentMonthlyPayment();
    const scenarioPayment = calculateAnnuityPayment(mortgageAmount, rate / 100, term * 12);

    const baseTotalInterest = (basePayment * months) - mortgageAmount;
    const scenarioTotalInterest = (scenarioPayment * months) - mortgageAmount;

    return scenarioTotalInterest - baseTotalInterest;
  };

  return (
    <div className="fade-in">
      <div className="card">
        <h1>Rente Analyse & Scenario's</h1>
        <p>
          Bekijk hoe verschillende rentepercentages uw maandlasten beïnvloeden en
          analyseer historische renteontwikkelingen om betere beslissingen te maken.
        </p>

        {currentRates && (
          <div className="alert alert-info" style={{ marginTop: '1rem' }}>
            <strong>Actuele hypotheekrente (DNB data - {currentRates.month}):</strong>
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
              {Object.entries(currentRates.rates).map(([period, rate]) =>
                rate && (
                  <span key={period} style={{
                    padding: '0.25rem 0.5rem',
                    backgroundColor: 'rgba(255,255,255,0.5)',
                    borderRadius: '4px',
                    fontSize: '0.9rem'
                  }}>
                    {period}: {Number(rate).toFixed(2)}%
                  </span>
                )
              ).filter(Boolean)}
            </div>
          </div>
        )}

        {ratesLoading && (
          <div className="alert alert-info" style={{ marginTop: '1rem' }}>
            <strong>Actuele rentes ophalen...</strong>
          </div>
        )}
      </div>

      <div className="card">
        <h2>Scenario Instellingen</h2>
        <div className="grid grid-3">
          <div className="form-group">
            <label htmlFor="mortgageAmount">
              Hypotheekbedrag (€)
              <input
                type="number"
                id="mortgageAmount"
                step="10000"
                value={mortgageAmount}
                onChange={(e) => setMortgageAmount(parseInt(e.target.value) || 0)}
              />
            </label>
          </div>

          <div className="form-group">
            <label htmlFor="term">
              Looptijd (jaar)
              <select
                id="term"
                value={term}
                onChange={(e) => setTerm(parseInt(e.target.value))}
              >
                <option value={10}>10</option>
                <option value={15}>15</option>
                <option value={20}>20</option>
                <option value={25}>25</option>
                <option value={30}>30</option>
              </select>
            </label>
          </div>

          <div className="form-group">
            <label htmlFor="baseRate">
              Basis rente (%)
              <input
                type="number"
                id="baseRate"
                step="0.1"
                min="0"
                max="15"
                value={baseRate}
                onChange={(e) => setBaseRate(parseFloat(e.target.value) || 0)}
              />
            </label>
            {currentRates && (
              <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.25rem' }}>
                Actuele DNB rentes:
                {Object.entries(currentRates.rates)
                  .map(([period, rate]) =>
                    rate ? ` ${period}: ${Number(rate).toFixed(2)}%` : null
                  )
                  .filter(Boolean)
                  .join(' |')}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-2">
        <div className="chart-container">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3>Historische Renteontwikkeling</h3>
            {availableFixations.length > 0 && (
              <select
                value={selectedFixation}
                onChange={(e) => setSelectedFixation(e.target.value)}
                style={{ padding: '0.25rem', fontSize: '0.9rem' }}
                disabled={historicalLoading}
              >
                {availableFixations.map(fixation => (
                  <option key={fixation} value={fixation}>
                    {getFixationLabel(fixation)}
                  </option>
                ))}
              </select>
            )}
          </div>
          {historicalLoading ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
              <strong>Historische rentes laden... (dit kan even duren)</strong>
            </div>
          ) : getHistoricalChartData().filteredCount > 0 ? (
            <Line
              data={getHistoricalChartData().chartData}
              options={{
                responsive: true,
                plugins: {
                  legend: {
                    position: 'top' as const,
                  },
                  title: {
                    display: false,
                  },
                },
                scales: {
                  x: {
                    title: {
                      display: true,
                      text: 'Jaar'
                    }
                  },
                  y: {
                    title: {
                      display: true,
                      text: 'Rente (%)'
                    },
                    beginAtZero: true
                  }
                }
              }}
            />
          ) : (
            <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
              {historicalRates.length === 0 ? 'Geen historische data beschikbaar' : 'Geen data beschikbaar voor geselecteerde periode'}
            </div>
          )}
        </div>

        <div className="chart-container">
          <h3>Maandlast per Rentescenario</h3>
          <Bar
            data={getScenarioChartData()}
            options={{
              responsive: true,
              plugins: {
                legend: {
                  position: 'top' as const,
                },
                title: {
                  display: false,
                },
              },
              scales: {
                x: {
                  title: {
                    display: true,
                    text: 'Rente (%)'
                  }
                },
                y: {
                  title: {
                    display: true,
                    text: 'Maandlast (€)'
                  },
                  beginAtZero: true
                }
              }
            }}
          />
        </div>
      </div>

      <div className="chart-container">
        <h3>Hypotheek Verloop bij {baseRate}% Rente over {term} Jaar</h3>
        <Line
          data={getRateComparisonData()}
          options={{
            responsive: true,
            interaction: {
              mode: 'index' as const,
              intersect: false,
            },
            plugins: {
              legend: {
                position: 'top' as const,
              },
              title: {
                display: false,
              },
            },
            scales: {
              x: {
                title: {
                  display: true,
                  text: 'Tijd'
                }
              },
              y: {
                type: 'linear' as const,
                display: true,
                position: 'left' as const,
                title: {
                  display: true,
                  text: 'Restschuld (€)'
                },
                beginAtZero: true
              },
              y1: {
                type: 'linear' as const,
                display: true,
                position: 'right' as const,
                title: {
                  display: true,
                  text: 'Jaarlijkse Betalingen (€)'
                },
                beginAtZero: true,
                grid: {
                  drawOnChartArea: false,
                }
              }
            }
          }}
        />
      </div>

      <div className="card">
        <h2>Rentescenario Analyse</h2>
        <p className="mb-3">
          <strong>Huidige instellingen:</strong> {formatCurrency(mortgageAmount)}
          over {term} jaar tegen {baseRate}% rente = <strong>{formatCurrency(getCurrentMonthlyPayment())}</strong> per maand
        </p>

        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Rente (%)</th>
                <th>Maandlast (€)</th>
                <th>Verschil (€/maand)</th>
                <th>Verschil Totale Rente (€)</th>
                <th>Impact</th>
              </tr>
            </thead>
            <tbody>
              {scenarios.map((scenario, index) => {
                const difference = getPaymentDifference(scenario.rate);
                const totalInterestDiff = getTotalInterestDifference(scenario.rate);
                const isBase = scenario.rate === baseRate;
                const isLower = scenario.rate < baseRate;

                return (
                  <tr key={index} className={isBase ? 'font-bold' : ''}>
                    <td className="text-center">
                      {scenario.rate.toFixed(1)}%
                      {isBase && <span className="text-accent"> (basis)</span>}
                    </td>
                    <td>{formatCurrency(scenario.monthlyPayment)}</td>
                    <td className={difference > 0 ? 'text-error' : difference < 0 ? 'text-success' : ''}>
                      {difference >= 0 ? '+' : ''}{formatCurrency(difference)}
                    </td>
                    <td className={totalInterestDiff > 0 ? 'text-error' : totalInterestDiff < 0 ? 'text-success' : ''}>
                      {totalInterestDiff >= 0 ? '+' : ''}{formatCurrency(totalInterestDiff)}
                    </td>
                    <td className="text-center">
                      {isBase ? '⚪' : isLower ? '🟢' : '🔴'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid grid-2">
        <div className="card">
          <h3>Tips voor Renteonderhandelingen</h3>
          <ul style={{ paddingLeft: '1.5rem' }}>
            <li>Vergelijk altijd meerdere aanbieders</li>
            <li>Onderhandel over de renteopslag</li>
            <li>Overweeg een kortere rentevastperiode bij hoge rentes</li>
            <li>Gebruik NHG voor een lagere rente</li>
            <li>Verhoog uw eigen inbreng voor betere condities</li>
            <li>Kies voor een energiezuinige woning</li>
          </ul>
        </div>

        <div className="card">
          <h3>Renteprognose Factoren</h3>
          <ul style={{ paddingLeft: '1.5rem' }}>
            <li>ECB-beleid en basisrente</li>
            <li>Inflatie en economische groei</li>
            <li>Vraag en aanbod op de hypotheekmarkt</li>
            <li>Bancaire concurrentie</li>
            <li>Internationale renteontwikkelingen</li>
            <li>Politieke en economische stabiliteit</li>
          </ul>
        </div>
      </div>

      <div className="card">
        <div className="alert alert-warning">
          <strong>Disclaimer:</strong> Deze renteanalyse is bedoeld ter indicatie.
          Werkelijke rentes kunnen afwijken en zijn afhankelijk van uw persoonlijke situatie,
          de gekozen hypotheekverstrekker en marktomstandigheden.
          Neem altijd contact op met een hypotheekadviseur voor actueel advies.
        </div>
      </div>
    </div>
  );
};

export default InterestRatePlots;
